import {
  Navigate,
  useRouterState,
} from "@tanstack/react-router"
import type { FC } from "react"
import { useEffect } from "react"
import { ThemeComponent } from "./ThemeComponent"
import {
  NavigationMenu,
  OverlayLoader,
} from "src/components"
import { WebSocketHandler } from "src/components/WebSocketHandler"
import {
  AccountContext,
  ApiContextProvider,
  DeviceContext,
  FollowedUsersProvider,
  RecentReadingsContextProvider,
  RefreshTokenHandler,
  useAuthContext,
  useFollowedUsers,
  useSessionStorageContext,
} from "src/contexts"
import {
  EventLoadingProvider,
  EntryLoadingProvider,
} from "src/contexts/LoadingContexts"
import { SegmentContextProvider } from "src/contexts/SegmentContext"
import { UpdateTitleEffect } from "src/hooks"
import {
  NotFound,
  TrackingTechnologyConsent,
} from "src/pages"
import type { RoutePath } from "src/types"

function ComponentOrNotFound(props: { Component: FC<object> }) {
  const { Component } = props

  const {
    currentUserId,
    isLoading,
  } = useFollowedUsers()

  if (isLoading) {
    return (<OverlayLoader visible/>)
  }

  if (currentUserId === undefined) {
    return <NotFound />
  }

  return <Component />
}

export function getAuthProtectedComponent(Component: FC<object>) {
  return function AuthProtectedComponent() {
    const currentPath = useRouterState({
      // note use .href if you want to include query params
      select: (s) => s.resolvedLocation.pathname,
    })

    const {
      isAuthenticated,
      baseURL,
    } = useAuthContext()

    const {
      logoutRedirect,
      setLoginRedirect,
    } = useSessionStorageContext()

    // in case the user session expires, save the current path as the login path
    useEffect(
      () => {
        if (isAuthenticated) {
          setLoginRedirect(currentPath as RoutePath)
        }
      },
      [
        setLoginRedirect,
        isAuthenticated,
        currentPath,
      ],
    )

    return isAuthenticated ? (
      <ApiContextProvider baseURL={baseURL ?? undefined}>
        <FollowedUsersProvider>
          <TrackingTechnologyConsent applyDefaultTheme>
            <ThemeComponent>
              {/*
                * children of TrackingTechnologyConsent aren't rendered until the
                * user responds to the Tracking Technology Consent prompt
                */}
              <AccountContext />
              <DeviceContext />
              <UpdateTitleEffect />
              <RefreshTokenHandler />
              <SegmentContextProvider>
                <WebSocketHandler baseURL={baseURL ?? ""}/>
                <EventLoadingProvider>
                  <EntryLoadingProvider>
                    <RecentReadingsContextProvider>
                      <NavigationMenu>
                        <ComponentOrNotFound Component={Component} />
                      </NavigationMenu>
                    </RecentReadingsContextProvider>
                  </EntryLoadingProvider>
                </EventLoadingProvider>
              </SegmentContextProvider>
            </ThemeComponent>
          </TrackingTechnologyConsent>
        </FollowedUsersProvider>
      </ApiContextProvider>
    ) : (
      <Navigate
        to={logoutRedirect}
        params={{}}
        search={{}}
        replace
      />
    )
  }
}
